import React, { useMemo } from 'react';
import { View } from 'react-native';
import { Button, Heading, Layout, Text } from '../../../components/core';
import {
  PageContent,
  PageError,
  PageFooter,
  PageHeader,
  PageLoading,
} from '../../../components/page';
import { SelfServiceCards } from '../../../components/recommendations/SelfServiceCards';
import { Portals } from '../../../content/portal/portals';
import { usePortalContext } from '../../../contexts/portalContext';
import { HubSurveyResultSupportArea, useHubSurveyResultQuery } from '../../../graphQL';
import { useScrollToRef } from '../../../hooks/useScrollToRef';
import { getRoute, useNavigate } from '../../../routes';
import { getStylesheet } from '../../../styles';
import { useTitle } from '../../../utils/useTitle';
import { RecommendationArea } from './components/RecommendationArea';
import { SavedResources } from './components/SavedResources';
import { SupportAreaButton } from './components/SupportAreaButton';
import { BookmarkTarget, useSurveyBookmarks } from './hooks/useSurveyBookmark';
import { useSurveyEligibilityRedirect } from './hooks/useSurveyEligibilityRedirect';
import { supportAreaIconMap } from './utils/supportAreaIconMap';

export const SurveyResultsPage = (): JSX.Element => {
  useTitle(['Student Survey', 'Results']);
  const { getRef, scrollToRef, containerRef } = useScrollToRef();
  const {
    data: surveyResultsData,
    loading: resultsLoading,
    error: resultsError,
  } = useHubSurveyResultQuery();
  const { onlyIfPortalIsActive } = usePortalContext();
  const connectNowEnabled = onlyIfPortalIsActive(Portals.ConnectNow, true, false);
  const showSelfCare = onlyIfPortalIsActive(Portals.Skills, true, false);
  const navigate = useNavigate();

  const { hasAccess } = useSurveyEligibilityRedirect({
    requiredStatus: 'Completed',
  });

  const supportAreas = surveyResultsData?.hubSurveyResult.supportAreas ?? [];
  const togetherAllEnabled = surveyResultsData?.hubSurveyResult.togetherAllEnabled ?? false;

  /** Memoized support area buttons to prevent unnecessary re-renders */
  const supportAreaButtons = useMemo(
    () =>
      supportAreas.map(supportArea => (
        <SupportAreaButton
          key={supportArea.supportArea}
          supportArea={supportArea}
          onPress={scrollToRef}
        />
      )),
    [supportAreas, scrollToRef],
  );

  const surveyResultId = surveyResultsData?.hubSurveyResult.id;
  const bookmarks = useSurveyBookmarks(surveyResultId);

  const bookmarkedResources = useMemo(() => {
    const allResources = supportAreas.flatMap(supportAreaItem =>
      supportAreaItem.recommendationAreas.flatMap(
        recommendationAreaItem => recommendationAreaItem.resources,
      ),
    );
    const uniqueResources = new Set<number>();
    return allResources.filter(resource => {
      if (!bookmarks.isBookmarked(resource.id)) return false;
      if (uniqueResources.has(resource.id)) return false;
      uniqueResources.add(resource.id);
      return true;
    });
  }, [supportAreas, bookmarks.bookmarkedIds, bookmarks]);

  if (!hasAccess || resultsLoading || bookmarks.loading) {
    return <PageLoading pageName="survey results" />;
  }

  if (resultsError || surveyResultsData?.hubSurveyResult.id == null) {
    return <PageError onRefreshPress={() => navigate(getRoute('home', {}))} />;
  }

  return (
    <PageContent testID="survey-results-page">
      <Layout.View ref={containerRef}>
        <PageHeader
          title="Review your personalized resources"
          undertext="Your responses have been submitted. Thank you so much for contributing to our understanding of how to best support students on your campus!"
          testID="survey-results-header"
        />

        {supportAreas.length > 0 && (
          <SavedResources bookmarkedResources={bookmarkedResources} bookmarkApi={bookmarks} />
        )}

        {supportAreas.length > 0 && (
          <Layout.Shift {...styles.supportAreaSection} alignNonMobile="center">
            <Layout.VStack {...styles.supportAreaContent} space={8}>
              <Layout.VStack space={3}>
                <Heading.h2 color="lightText" level={2}>
                  Your recommended support areas
                </Heading.h2>

                <Text.para color="lightText">
                  Based on your answers, we've identified {supportAreas.length}
                  {supportAreas.length === 1 ? ' area' : ' areas'} where you may benefit from
                  additional support. Select any category below to explore relevant recommendations
                  and available resources.
                </Text.para>
              </Layout.VStack>

              <Layout.Shift
                {...styles.supportAreaButtons}
                aria-label="Support Areas"
                role="navigation"
              >
                {supportAreaButtons}
              </Layout.Shift>
            </Layout.VStack>
          </Layout.Shift>
        )}

        <Layout.VStack space={6} paddingTop={2}>
          {supportAreas.length === 0 ? (
            <NoRecommendations />
          ) : (
            <RecommendationsList
              orderedSupportAreas={supportAreas}
              getSectionRef={getRef}
              bookmarkApi={bookmarks}
            />
          )}
        </Layout.VStack>

        <Layout.VStack paddingTop={6} aria-label="Additional self-serve tools" role="region">
          <SelfServiceCards
            showConnectNow={connectNowEnabled}
            showTogetherall={togetherAllEnabled}
            showSelfCare={showSelfCare}
            headingLevel={2}
          />
        </Layout.VStack>
        <PageFooter />
      </Layout.View>
    </PageContent>
  );
};

const NoRecommendations = (): JSX.Element => {
  const navigate = useNavigate();

  return (
    <Layout.VStack
      {...styles.noRecommendationArea}
      space={4}
      aria-label="No Recommendations"
      role="region"
    >
      <Heading.h3 level={2}>
        According to your responses, it seems like you have access to the support and resources you
        need right now!
      </Heading.h3>
      <Text.para>
        That being said, we want to acknowledge that a survey cannot catch every single "need" a
        student might have, so if you're feeling like there is a type of support that you want or
        need that isn't covered in our recommendations, we recommend you start with talking with
        someone you trust (an advisor, professor, counselor, or other supportive person) about what
        options might be best for you.
      </Text.para>
      <Text.para>
        We also have a list of campus resources across a variety of support areas right here within
        the Mantra Hub that you can browse at any time.
      </Text.para>
      <Button.secondaryLarge
        testID="button-to-campus-resources"
        isLink
        onPress={() => navigate(getRoute('campusResources', {}))}
      >
        View Campus Resources
      </Button.secondaryLarge>
    </Layout.VStack>
  );
};

export type BookmarkApi = {
  isBookmarked: (id: number) => boolean;
  toggle: (target: BookmarkTarget) => Promise<void>;
};

const RecommendationsList = ({
  orderedSupportAreas,
  getSectionRef,
  bookmarkApi,
}: {
  orderedSupportAreas: HubSurveyResultSupportArea[];
  getSectionRef: (supportArea: string) => React.RefObject<View>;
  bookmarkApi: BookmarkApi;
}): JSX.Element => {
  return (
    <Layout.VStack aria-label="Recommendations" role="region">
      <Layout.VStack space={6} paddingTop={4} aria-label="Support Area List" role="list">
        {orderedSupportAreas.map(
          ({ supportArea, recommendationAreas, title: supportAreaTitle }) => {
            const IconComponent = supportAreaIconMap[supportArea]?.default;

            const headerId = `support-area-${supportArea}`;
            return (
              <Layout.VStack space={4} role="listitem" key={supportArea}>
                <Layout.HStack
                  {...styles.orderedSupportAreaTitle}
                  space={2}
                  aria-labelledby={headerId}
                  ref={getSectionRef(supportArea)}
                >
                  {IconComponent !== undefined && <IconComponent size={8} aria-hidden />}
                  <Heading.h3 id={headerId} paddingLeft={2}>
                    {supportAreaTitle}
                  </Heading.h3>
                </Layout.HStack>

                {recommendationAreas.map(area => {
                  return (
                    <RecommendationArea
                      key={area.recommendationArea}
                      recommendationArea={area}
                      bookmarkApi={bookmarkApi}
                    />
                  );
                })}
              </Layout.VStack>
            );
          },
        )}
      </Layout.VStack>
    </Layout.VStack>
  );
};

const styles = getStylesheet({
  noRecommendationArea: {
    backgroundColor: 'white',
    borderColor: 'secondary.300',
    borderWidth: 1,
    borderRadius: 16,
    padding: 8,
  },

  orderedSupportAreaTitle: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },

  supportAreaButtons: {
    flexWrap: 'wrap',
    gap: 3,
    width: '100%',
    justifyContent: 'flex-start',
  },

  supportAreaContent: {
    flex: 1,
  },

  supportAreaSection: {
    borderRadius: '8px',
    backgroundColor: 'mantraNavy',
    gap: 8,
    padding: 8,
    marginBottom: 6,
  },
});
